import React, { useState } from 'react';
import { Settings as SettingsIcon, ArrowLeft, Code } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';

interface SettingsProps {
  onBack: () => void;
  currentState: {
    acquisition: number;
    retention: number;
    shareOfWallet: number;
    initialCustomers: number;
    monthlyRevenue: number;
    yAxisMaxValue: number;
  };
}

export function Settings({ onBack, currentState }: SettingsProps) {
  const { settings, updateSettings } = useSettings();
  const [formData, setFormData] = useState({
    initialCustomers: settings.initialCustomers,
    monthlyRevenue: settings.monthlyRevenue,
    yAxisMaxValue: settings.yAxisMaxValue,
  });
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateSettings(formData);
    onBack();
  };

  const handleCopyEmbed = () => {
    // Create a URL with all current parameters
    const url = new URL(window.location.href);
    url.searchParams.set('embed', 'true');
    url.searchParams.set('acquisition', currentState.acquisition.toString());
    url.searchParams.set('retention', currentState.retention.toString());
    url.searchParams.set('shareOfWallet', currentState.shareOfWallet.toString());
    url.searchParams.set('initialCustomers', currentState.initialCustomers.toString());
    url.searchParams.set('monthlyRevenue', currentState.monthlyRevenue.toString());
    url.searchParams.set('yAxisMaxValue', currentState.yAxisMaxValue.toString());
    
    const embedCode = `<iframe
  src="${url.toString()}"
  width="100%"
  height="500"
  frameborder="0"
  style="border-radius: 8px; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);"
></iframe>`;
    
    navigator.clipboard.writeText(embedCode).then(() => {
      setShowCopiedMessage(true);
      setTimeout(() => setShowCopiedMessage(false), 2000);
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex items-center gap-2 mb-6">
        <SettingsIcon className="w-6 h-6 text-blue-600" />
        <h1 className="text-2xl font-bold text-gray-800">Initial Settings</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Initial Number of Customers
          </label>
          <input
            type="number"
            min="1"
            value={formData.initialCustomers}
            onChange={(e) => setFormData(prev => ({ ...prev, initialCustomers: Number(e.target.value) }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Initial Monthly Revenue per Customer ($)
          </label>
          <input
            type="number"
            min="1"
            value={formData.monthlyRevenue}
            onChange={(e) => setFormData(prev => ({ ...prev, monthlyRevenue: Number(e.target.value) }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Y-Axis Initial Range ($)
          </label>
          <input
            type="number"
            min="100000"
            step="100000"
            value={formData.yAxisMaxValue}
            onChange={(e) => setFormData(prev => ({ ...prev, yAxisMaxValue: Number(e.target.value) }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
          <p className="mt-1 text-sm text-gray-500">
            The chart will auto-adjust if revenue exceeds {((formData.yAxisMaxValue * 0.9) / 1000000).toFixed(1)}M
          </p>
        </div>

        <div className="border-t pt-6">
          <button
            type="button"
            onClick={handleCopyEmbed}
            className="w-full mb-4 flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Code className="w-4 h-4" />
            {showCopiedMessage ? 'Copied!' : 'Copy Embed Code'}
          </button>
        </div>

        <div className="flex gap-4">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <button
            type="submit"
            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md shadow-sm text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Save Changes
          </button>
        </div>
      </form>
    </div>
  );
}